import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AppleGlyph, Banner, Button, ClaimWoo, Field, GoogleGlyph, Wordmark } from '@cowoo/shared/ui-web';
import { STALE_TIME, authErrorKey, emailSchema, mapAuthError, qk } from '@cowoo/shared';
import { env } from '../../env';
import { api } from '../../lib/api';
import { OAUTH_REDIRECT_URI, auth } from '../../lib/auth';
import { rememberOtpMethod } from './pending';

/**
 * `/login` (09-web-member § 4, artboard `DesktopLogin.dc.html`).
 *
 * Two columns from 1024 px, the brand panel hidden below that — so the panel
 * carries nothing the form needs. The form column is the artboard's 420 px
 * rather than a Tailwind `max-w-*` step, because the design fixes it.
 *
 * Type sizes come from the documented scale of 03 § 2 (`text-webH1`,
 * `text-webH2`, …), not from Tailwind's own `text-3xl`/`text-4xl`, which are
 * 30 px and 36 px — sizes the scale does not contain. Where the artboard and
 * 03 § 2 disagree on a step, 03 wins by the conflict order (OI-F-15).
 */
export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [error, setError] = useState<string | null>(
    // § 4: an OAuth failure returns here as `?error=oauth`.
    search.get('error') === 'oauth' ? t('authErrors.oauth_failed', { provider: 'OAuth' }) : null,
  );
  const [busy, setBusy] = useState<'email' | 'apple' | 'google' | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(t('authErrors.email_invalid'));
      return;
    }

    setBusy('email');
    setError(null);
    try {
      // The code is bound to a `methodId`, not to the address, so the id has to
      // survive the navigation to `/login/code`.
      const { methodId } = await auth.sendOtp(parsed.data);
      rememberOtpMethod(parsed.data, methodId);
      navigate('/login/code');
    } catch (thrown) {
      const key = authErrorKey(mapAuthError(thrown));
      setError(key ? t(key) : null);
    } finally {
      setBusy(null);
    }
  }

  async function oauth(provider: 'apple' | 'google'): Promise<void> {
    setBusy(provider);
    setError(null);
    try {
      window.location.assign(await auth.startOAuth(provider, OAUTH_REDIRECT_URI));
    } catch {
      setError(t('authErrors.oauth_failed', { provider: provider === 'apple' ? 'Apple' : 'Google' }));
      setBusy(null);
    }
  }

  const disabled = busy !== null;

  return (
    <main className="grid min-h-screen lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center px-5 py-12 lg:p-12">
        <div className="flex w-full max-w-[420px] flex-col gap-3.5">
          <h1 className="text-webH2 text-ink">{t('member.login.title')}</h1>
          <p className="text-webSmall text-muted pb-3">{t('member.login.body')}</p>

          <Button
            variant="ink"
            onClick={() => void oauth('apple')}
            loading={busy === 'apple'}
            disabled={disabled}
            full
          >
            <AppleGlyph />
            {t('member.login.apple')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void oauth('google')}
            loading={busy === 'google'}
            disabled={disabled}
            full
          >
            <GoogleGlyph />
            {t('member.login.google')}
          </Button>

          <div className="flex items-center gap-3 py-2">
            <span className="bg-line h-px flex-1" />
            <span className="text-webEyebrow text-muted normal-case tracking-normal">
              {t('member.login.divider')}
            </span>
            <span className="bg-line h-px flex-1" />
          </div>

          <form onSubmit={submit} className="flex flex-col gap-3.5">
            <Field
              label={t('member.login.email')}
              placeholder={t('member.login.emailPlaceholder')}
              name="email"
              type="email"
              required
              disabled={disabled}
              autoComplete="email"
            />
            <Button type="submit" loading={busy === 'email'} disabled={disabled} full>
              {t('member.login.submit')}
            </Button>
            {error ? <Banner tone="danger">{error}</Banner> : null}
          </form>

          <p className="text-webEyebrow text-muted normal-case tracking-normal">{t('member.login.hint')}</p>
          <p className="text-webEyebrow text-muted pt-3 font-normal normal-case tracking-normal">
            {/* eslint-disable jsx-a11y/anchor-has-content --
                `Trans` fills these from the German sentence in `de.json`;
                writing the link text here would hard-code copy (D-008). */}
            <Trans
              i18nKey="member.login.terms"
              components={{
                agb: <a className="underline" href={`${env.SITE_URL}/rechtliches#agb`} />,
                privacy: <a className="underline" href={`${env.SITE_URL}/rechtliches#datenschutz`} />,
              }}
            />
            {/* eslint-enable jsx-a11y/anchor-has-content */}
          </p>
        </div>
      </div>
    </main>
  );
}

/**
 * The left column of § 4.
 *
 * The photo is dimmed and then covered by a top-to-bottom teal gradient, so the
 * wordmark reads against the bright part of the image and the headline against
 * the dark part — the panel has to work with any crop the photo happens to get
 * at this viewport. The chips are the open locations plus the Loft while it is
 * not open yet, from `/public/locations`, never from a constant.
 */
function BrandPanel() {
  const { t } = useTranslation();
  const locations = useQuery({
    queryKey: qk.public.locations(),
    staleTime: STALE_TIME.publicLocations,
    queryFn: async () => {
      const { data, error } = await api.GET('/public/locations');
      if (error || !data) throw error ?? new Error('locations unavailable');
      return data.items;
    },
  });

  const chips = (locations.data ?? []).filter((l) => l.status === 'open' || l.key === 'loft');

  return (
    <aside className="bg-teal-dark relative hidden overflow-hidden lg:block">
      <img src="/loft.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-55" />
      <div className="from-teal-dark/15 to-teal-dark/85 absolute inset-0 bg-gradient-to-b" />
      <div className="relative flex h-full flex-col justify-between px-14 py-12 text-white">
        <Wordmark />
        <div className="flex max-w-[520px] flex-col gap-[18px]">
          <h2 className="text-webH1">
            <ClaimWoo text={t('member.login.panelClaim')} />
          </h2>
          <p className="text-webBody opacity-90">{t('member.login.panelBody')}</p>
          <ul className="flex flex-wrap gap-2.5">
            {chips.map((location) => (
              <li
                key={location.id}
                // 32 px, as the board draws the chip — the eyebrow's line
                // height made it 34 and the design check no longer read it as one.
                className="text-webEyebrow rounded-pill inline-flex h-8 items-center bg-white/[0.16] px-3.5 normal-case tracking-normal"
              >
                {`${location.name} · ${location.badge}`}
                {location.status === 'open' ? null : ` · ${t('member.login.panelSoon')}`}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}
